import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
// @mui
import { styled, alpha } from '@mui/material/styles';
import { Box, Link, Button, Drawer, Typography, Avatar, Stack, Snackbar, Alert } from '@mui/material';
import Iconify from '../../../components/iconify';
// mock
import account from '../../../_mock/account';
// hooks
import useResponsive from '../../../hooks/useResponsive';
// components
import Logo from '../../../components/logo';
import Scrollbar from '../../../components/scrollbar';
import NavSection from '../../../components/nav-section';
import {useAppContext} from '../../../context';
//
import navConfig from './config';

// ----------------------------------------------------------------------

const NAV_WIDTH = 280;

const StyledAccount = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(2, 2.5),
  borderRadius: Number(theme.shape.borderRadius) * 1.5,
  backgroundColor: alpha(theme.palette.grey[500], 0.12),
}));

// ----------------------------------------------------------------------

Nav.propTypes = {
  openNav: PropTypes.bool,
  onCloseNav: PropTypes.func,
};

export default function Nav({ openNav, onCloseNav }) {
  const { pathname } = useLocation();
  const {user, setUser} = useAppContext();
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const isDesktop = useResponsive('up', 'lg');

  const getReferralLink = () => {
    if (!user?.user?.email) return '';
    const baseUrl = window.location.origin;
    return `${baseUrl}/signup?referrer=${encodeURIComponent(user.user.email)}`;
  };

  const handleShareClick = async () => {
    const referralLink = getReferralLink();

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Gino Boxing Studio',
          text: 'Rejoins-moi à Gino Boxing Studio !',
          url: referralLink,
        });
      } catch (err) {
        // User cancelled or share failed, fallback to copy
        copyToClipboard(referralLink);
      }
    } else {
      copyToClipboard(referralLink);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setSnackbarOpen(true);
  };

  useEffect(() => {
    if (openNav) {
      onCloseNav();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);


  if (!user)
  return <div />

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        '& .simplebar-content': { minHeight: '100%', display: 'flex', flexDirection: 'column' },
      }}
    >
      <Box sx={{ px: 2.5, py: 3, display: 'inline-flex' }}>
        <Logo />
      </Box>

      <Box sx={{ mb: 5, mx: 2.5 }}>
        <Link underline="none">
          <StyledAccount>
            {/* <Avatar src={account.photoURL} alt="photoURL" /> */}

            <Box sx={{ ml: 2 }}>
              <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>
                {user.user.firstname} {user.user.lastname}
              </Typography>

              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {account.role}
              </Typography>
            </Box>
          </StyledAccount>
        </Link>
      </Box>

      <NavSection data={navConfig} />

      <Box sx={{ flexGrow: 1 }} />

      {/* Referral Section */}
      <Box sx={{ mx: 2.5, mb: 2, p: 2, borderRadius: 1.5, bgcolor: alpha('#919EAB', 0.08) }}>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
          Envie d'une séance offerte ? Ramène un +1 !
        </Typography>
        <Button
          fullWidth
          variant="outlined"
          size="small"
          startIcon={<Iconify icon="eva:share-fill" />}
          onClick={handleShareClick}
        >
          Partager mon lien
        </Button>
      </Box>

      <Box sx={{ px: 2.5, pb: 2 }}>
        <Link
          href="/dashboard/cgv"
          underline="hover"
          sx={{
            display: 'block',
            textAlign: 'center',
            color: 'text.secondary',
            fontSize: '0.875rem',
            mb: 1
          }}
        >
          Conditions Générales de Vente
        </Link>
      </Box>

      <Button variant="contained" onClick={()=> {
        setUser(null);
      }}>
        Déconnexion
      </Button>


    </Scrollbar>
  );

  return (
    <Box
      component="nav"
      sx={{
        flexShrink: { lg: 0 },
        width: { lg: NAV_WIDTH },
      }}
    >
      {isDesktop ? (
        <Drawer
          open
          variant="permanent"
          PaperProps={{
            sx: {
              width: NAV_WIDTH,
              bgcolor: 'background.default',
              borderRightStyle: 'dashed',
            },
          }}
        >
          {renderContent}
        </Drawer>
      ) : (
        <Drawer
          open={openNav}
          onClose={onCloseNav}
          ModalProps={{
            keepMounted: true,
          }}
          PaperProps={{
            sx: { width: NAV_WIDTH },
          }}
        >
          {renderContent}
        </Drawer>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity="success" sx={{ width: '100%' }}>
          Lien copié dans le presse-papiers !
        </Alert>
      </Snackbar>
    </Box>
  );
}
